import React, { useRef, useEffect } from 'react';

interface TextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
  readOnly?: boolean;
}

const TextEditor: React.FC<TextEditorProps> = ({
  value,
  onChange,
  placeholder = "Commencez à écrire...",
  height = "400px",
  readOnly = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const caretPositionRef = useRef<number | null>(null);
  const scrollPositionRef = useRef<number>(0);

  // Save caret and scroll position
  const saveCaretPosition = () => {
    if (!editorRef.current) return;
    
    // Save scroll position
    scrollPositionRef.current = editorRef.current.scrollTop;
    
    // Save caret position
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editorRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      caretPositionRef.current = preCaretRange.toString().length;
    }
  };

  // Restore caret and scroll position
  const restoreCaretPosition = () => {
    if (!editorRef.current || caretPositionRef.current === null) return;

    // Restore scroll position first
    editorRef.current.scrollTop = scrollPositionRef.current;

    // Then restore caret position
    const selection = window.getSelection();
    const range = document.createRange();
    let currentLength = 0;
    let foundPosition = false;

    function traverseNodes(node: Node) {
      if (foundPosition) return;

      if (node.nodeType === Node.TEXT_NODE) {
        const nodeLength = node.textContent?.length || 0;
        if (currentLength + nodeLength >= caretPositionRef.current) {
          range.setStart(node, caretPositionRef.current - currentLength);
          range.setEnd(node, caretPositionRef.current - currentLength);
          foundPosition = true;
        }
        currentLength += nodeLength;
      } else {
        for (const childNode of Array.from(node.childNodes)) {
          traverseNodes(childNode);
        }
      }
    }

    traverseNodes(editorRef.current);

    if (selection && foundPosition) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  // Handle input changes
  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    saveCaretPosition();
    const content = e.currentTarget.innerHTML;
    onChange(content);
  };

  // Update editor content when value changes
  useEffect(() => {
    if (!editorRef.current) return;

    // Only update if content actually changed
    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        restoreCaretPosition();
        
        // Double RAF to ensure smooth scrolling
        requestAnimationFrame(() => {
          if (editorRef.current) {
            editorRef.current.scrollTop = scrollPositionRef.current;
          }
        });
      });
    }
  }, [value]);

  return (
    <div className="text-editor-wrapper" style={{ height }}>
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        onInput={handleInput}
        className="text-editor"
        data-placeholder={placeholder}
        onScroll={() => {
          if (editorRef.current) {
            scrollPositionRef.current = editorRef.current.scrollTop;
          }
        }}
      />
      <style>{`
        .text-editor-wrapper {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          background: ${readOnly ? '#f9fafb' : '#ffffff'};
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .text-editor {
          flex: 1;
          width: 100%;
          height: 100%;
          padding: 1.5rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 16px;
          line-height: 1.8;
          color: #374151;
          background: transparent;
          border: none;
          resize: none;
          overflow-y: auto;
          cursor: text;
          white-space: pre-wrap;
        }

        .text-editor:focus {
          outline: none;
        }

        .text-editor:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          font-style: italic;
        }

        .text-editor strong {
          font-weight: 800;
          font-size: 1.1em;
          color: #111827;
          display: block;
          margin-bottom: 0.5rem;
          letter-spacing: -0.01em;
          background: linear-gradient(to right, #1a1a1a, #333333);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .text-editor br {
          display: block;
          content: "";
          margin-top: 1rem;
        }

        /* Add more space between sections */
        .text-editor strong + br + br {
          margin-top: 2rem;
        }

        /* Preserve emojis */
        .text-editor img.emoji {
          height: 1em;
          width: 1em;
          margin: 0 .05em 0 .1em;
          vertical-align: -0.1em;
        }

        /* Webkit scrollbar */
        .text-editor::-webkit-scrollbar {
          width: 12px;
          background-color: #f5f5f5;
          display: block !important;
        }

        .text-editor::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 6px;
        }

        .text-editor::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 6px;
          border: 3px solid #f1f1f1;
        }

        .text-editor::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }

        /* Firefox scrollbar */
        .text-editor {
          scrollbar-width: thin;
          scrollbar-color: #c1c1c1 #f1f1f1;
        }
      `}</style>
    </div>
  );
};

export default TextEditor;